/*
 * Copyright (C) 2021 Quartet Health, Inc - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 */
import { GlobeIcon } from '@coop/ole/Icons/GlobeIcon';
import { colors } from '@coop/ole/styles/colors';
import fonts, { fontSizes } from '@coop/ole/styles/fonts';
import layerTypes from '@coop/ole/styles/layers';
import spacing from '@coop/ole/styles/spacing';
import stacking from '@coop/ole/styles/stacking';
import {
  ListboxButton,
  ListboxInput,
  ListboxList,
  ListboxOption,
  ListboxPopover
} from '@reach/listbox';
import * as React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { BCP47LanguageTag } from './types';

const StyleOverrides = createGlobalStyle`
  :root {
    --reach-listbox: 1;
  }

  [data-reach-listbox-input] {
    position: relative;
    :hover {
      cursor: pointer;
    }
  }

  [data-reach-listbox-popover] {
    display: block;
    position: absolute;
    min-width: 136px;
    right: 0;
    transform: translateY(10px);
    ${stacking.menu}
    border-radius: 0;
  }

  [data-reach-listbox-popover]:focus-within [data-reach-listbox-list] {
    box-shadow: 0 0 0 1px ${colors.lightPurple};
  }

  [data-reach-listbox-popover][hidden] {
    display: none;
  }

  [data-reach-listbox-list] {
    margin: 0;
    padding: 0;
    list-style: none;
    ${layerTypes.overlay}
    overflow: hidden;
  }

  [data-reach-listbox-list]:focus {
    box-shadow: none;
    outline: none;
  }

  [data-reach-listbox-option] {
    display: block;
    margin: 0;
    white-space: nowrap;
    user-select: none;
    cursor: pointer;
    text-decoration: initial;
    font: ${fonts.normal(fontSizes.body)};
    padding: ${spacing.xs} ${spacing.xxs};
    color: ${colors.grey100};
    text-align: left;
  }

  [data-reach-listbox-option][aria-selected='true'] {
    background: ${colors.brandPurple};
    color: ${colors.white};
    outline: none;
  }

  [data-reach-listbox-option][data-current] {
    font-weight: bolder;
  }
`;

const UnstyledButton = styled.button`
  display: flex;
  align-items: center;
  border: none;
  padding: 0;
  width: auto;
  overflow: visible;
  background: transparent;
  color: inherit;
  font: inherit;
  line-height: normal;
  cursor: pointer;
  -webkit-font-smoothing: inherit;
  -moz-osx-font-smoothing: inherit;
  -webkit-appearance: none;
  justify-self: center;
  align-self: center;
`;

interface LanguageSelectorProps {
  label: string;
  language: BCP47LanguageTag;
  onChange: (language: BCP47LanguageTag) => void;
  onButtonClick?: () => void;
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  label,
  language,
  onChange,
  onButtonClick
}) => {
  return (
    <React.Fragment>
      <StyleOverrides />

      <ListboxInput value={language} onChange={onChange}>
        <ListboxButton
          as={UnstyledButton}
          aria-label={label}
          onClick={onButtonClick}
        >
          <GlobeIcon
            stroke="currentColor"
            aria-hidden={true}
            width={15}
            height={15}
          />
          <span
            style={{
              marginLeft: spacing.xxxs,
              font: fonts.bold(fontSizes.body)
            }}
            data-value={language}
          >
            {language === 'en-US' && 'EN'}
            {language === 'es-US' && 'ES'}
          </span>
        </ListboxButton>
        <ListboxPopover portal={false}>
          <ListboxList>
            <ListboxOption value="en-US">English</ListboxOption>
            <ListboxOption value="es-US">Español</ListboxOption>
          </ListboxList>
        </ListboxPopover>
      </ListboxInput>
    </React.Fragment>
  );
};
